import React, { useEffect, useMemo, useState } from "react";
import useInventoryApi from "./useInventoryApi";
import WidgetPanel from "../../ui/WidgetPanel";
import { num } from "../../Analytics/metricValue";
import "../OpsShared.css";
import "../../Analytics/pages/AnalyticsShared.css";
import "../../../styles/data-table.css";

const TYPES = [
  ["all", "All types"], ["receive", "Receive"], ["consume", "Consume"],
  ["transfer_in", "Transfer in"], ["transfer_out", "Transfer out"],
  ["adjust", "Adjust"], ["return", "Return"],
];
const MOVE_TYPES = TYPES.slice(1); // no "all" in the form

const EMPTY = { type:"receive", product_id:"", qty:"", uom:"ea", location_id:"", lot:"", expiry:"", note:"" };

const qtyCell = (v) => {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString();
};

export default function StockPage(){
  const { listStock, postStock, listProducts } = useInventoryApi();
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);

  const load = async ()=>{
    setLoading(true);
    try{
      const [s, p] = await Promise.all([listStock({ limit:200 }), listProducts({ limit:500 })]);
      setRows(Array.isArray(s)?s:(s.results||[]));
      setProducts(Array.isArray(p)?p:(p.results||[]));
    } finally{ setLoading(false); }
  };
  useEffect(()=>{ load(); },[]); // eslint-disable-line

  const onSubmit = async e=>{
    e.preventDefault();
    const payload = { ...form, qty: form.qty===""?null:Number(form.qty), product: form.product_id };
    await postStock(payload);
    setForm(f=>({ ...f, qty:"", lot:"", expiry:"", note:"" }));
    await load();
  };

  const filtered = useMemo(()=> rows.filter(r=>{
    if(type!=="all" && r.type!==type) return false;
    if(q){
      const blob = `${r.product_sku} ${r.product_name} ${r.location_name} ${r.note||""}`.toLowerCase();
      if(!blob.includes(q.toLowerCase())) return false;
    }
    return true;
  }),[rows,q,type]);

  return (
    <div className="ops-page">
      <header className="ops-head">
        <div>
          <h1>Stock movements</h1>
          <div className="ops-muted">Every receive, transfer, count and adjustment, newest first.</div>
        </div>
        <div className="ops-actions">
          <input className="ops-search" placeholder="Search SKU, product, location…" value={q} onChange={e=>setQ(e.target.value)} />
        </div>
      </header>

      <div className="analytics-chip-row" role="tablist" aria-label="Movement type">
        {TYPES.map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`analytics-chip analytics-chip--button ${type === k ? "is-active" : ""}`}
            onClick={() => setType(k)}
          >
            {l}
          </button>
        ))}
      </div>

      <section className="ops-section">
        <div className="ops-section__head"><h3>Quick movement</h3></div>
        <form className="ops-form" onSubmit={onSubmit}>
          <div className="ops-field"><label>Type</label>
            <select name="type" value={form.type} onChange={e=>setForm(f=>({ ...f, type:e.target.value }))}>
              {MOVE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="ops-field"><label>Product</label>
            <select name="product_id" value={form.product_id} onChange={e=>setForm(f=>({ ...f, product_id:e.target.value }))}>
              <option value="">—</option>
              {products.map(p=><option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
            </select>
          </div>
          <div className="ops-field"><label>Qty</label><input type="number" value={form.qty} onChange={e=>setForm(f=>({ ...f, qty:e.target.value }))} /></div>
          <div className="ops-field"><label>Unit of measure</label><input value={form.uom} onChange={e=>setForm(f=>({ ...f, uom:e.target.value }))} /></div>
          <div className="ops-field"><label>Location ID</label><input value={form.location_id} onChange={e=>setForm(f=>({ ...f, location_id:e.target.value }))} /></div>
          <div className="ops-field"><label>Lot</label><input value={form.lot} onChange={e=>setForm(f=>({ ...f, lot:e.target.value }))} /></div>
          <div className="ops-field"><label>Expiry</label><input type="date" value={form.expiry} onChange={e=>setForm(f=>({ ...f, expiry:e.target.value }))} /></div>
          <div className="ops-field"><label>Note</label><input value={form.note} onChange={e=>setForm(f=>({ ...f, note:e.target.value }))} /></div>
          <div className="ops-form__actions">
            <button className="ops-btn secondary" type="button" onClick={()=>setForm(EMPTY)}>Clear</button>
            <button className="ops-btn" type="submit">Log</button>
          </div>
        </form>
      </section>

      <WidgetPanel className="gt-table-shell" padded={false}>
        <div className="gt-table-scroll">
          <div className="gt-table-wrapper">
            <table className="gt-table">
              <thead>
                <tr><th>When</th><th>Type</th><th>SKU</th><th>Name</th><th className="num">Qty</th><th>UoM</th><th>Location</th><th>Lot</th><th>Expiry</th><th>Note</th></tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={10}><div className="gt-empty">Loading…</div></td></tr>
                ) : filtered.length === 0 ? (
                  <tr className="gt-row gt-row--static"><td className="gt-cell" colSpan={10}><div className="gt-empty">No movements match this filter.</div></td></tr>
                ) : filtered.map(m=>(
                  <tr key={m.id} className="gt-row gt-row--static">
                    <td className="gt-cell">{(m.created_at||"").slice(0,10) || "—"}</td>
                    <td className="gt-cell cap">{(m.type||"").replace(/_/g, " ") || "—"}</td>
                    <td className="gt-cell mono">{m.product_sku || "—"}</td>
                    <td className="gt-cell name-col">{m.product_name || "—"}</td>
                    <td className="gt-cell num">{qtyCell(m.qty)}</td>
                    <td className="gt-cell">{m.uom||"ea"}</td>
                    <td className="gt-cell">{m.location_name||"—"}</td>
                    <td className="gt-cell mono">{m.lot||"—"}</td>
                    <td className="gt-cell">{m.expiry||"—"}</td>
                    <td className="gt-cell gt-cell--muted">{m.note||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="gt-table-footer">
          <div className="gt-table-count">Showing <strong>{filtered.length}</strong> of <strong>{rows.length}</strong> movements</div>
        </div>
      </WidgetPanel>
    </div>
  );
}
